/**
 * Seed gRPC-Web Service Layer
 * Provides API for Model Management operations
 */

import { SeedClient } from '../generated/SeedServiceClientPb.ts';
import {
  Empty,
  ModelConfigList,
  ModelConfigEntry,
  AddModelRequest,
  TaskType,
} from '../generated/seed_pb.js';

// Envoy proxy URL for Seed gRPC server
const SEED_GRPC_URL = process.env.REACT_APP_SEED_GRPC_URL || 'http://129.128.215.152:8081';

// Create singleton client instance
const client = new SeedClient(SEED_GRPC_URL, null, null);

/**
 * Health Check - Test Seed gRPC server connectivity
 */
export const healthCheck = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[SeedGrpcService] Checking health...');
    const request = new Empty();

    client.health(request, {}, (err, response) => {
      if (err) {
        console.error('[SeedGrpcService] Health check error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[SeedGrpcService] Health check success:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Get all model configurations
 */
export const getModelConfigs = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[SeedGrpcService] Getting model configurations...');
    const request = new Empty();

    client.getModelConfigs(request, {}, (err, response) => {
      if (err) {
        console.error('[SeedGrpcService] GetModelConfigs error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[SeedGrpcService] GetModelConfigs success:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Update model configurations
 */
export const updateModelConfigs = (configs: any[]): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[SeedGrpcService] Updating model configurations...');
    console.log('[SeedGrpcService] Configs to update:', configs);

    const configList = new ModelConfigList();

    // Build ModelConfigEntry objects
    configs.forEach((config) => {
      const entry = new ModelConfigEntry();
      const taskTypeNum = taskTypeToNumber(config.taskType);

      console.log(`[SeedGrpcService] Converting taskType: ${config.taskType} (${typeof config.taskType}) -> ${taskTypeNum}`);

      entry.setModelName(config.modelName || '');
      entry.setModelPath(config.modelPath || '');
      // Convert taskType from string name to number if needed
      entry.setTaskType(taskTypeNum);
      entry.setJsonConfig(config.jsonConfig || '');
      entry.setInterSup(Boolean(config.interSup));
      entry.setTileWidth(Number(config.tileWidth) || 0);
      entry.setTileHeight(Number(config.tileHeight) || 0);

      configList.addConfigs(entry);
    });

    client.updateModelConfigs(configList, {}, (err, response) => {
      if (err) {
        console.error('[SeedGrpcService] UpdateModelConfigs error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[SeedGrpcService] UpdateModelConfigs success:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Add a new model from S3
 */
export const addModelFromS3 = (modelData: {
  modelName: string;
  modelS3Uri: string;
  jsonConfigS3Uri: string;
  taskType: number;
  interSup: boolean;
  tileWidth: number;
  tileHeight: number;
}): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[SeedGrpcService] Adding model from S3...');
    console.log('[SeedGrpcService] Model data:', modelData);

    const request = new AddModelRequest();
    request.setModelName(modelData.modelName);
    request.setModelS3Uri(modelData.modelS3Uri);
    request.setJsonConfigS3Uri(modelData.jsonConfigS3Uri);
    // Ensure types are correct
    request.setTaskType(taskTypeToNumber(modelData.taskType));
    request.setInterSup(Boolean(modelData.interSup));
    request.setTileWidth(Number(modelData.tileWidth));
    request.setTileHeight(Number(modelData.tileHeight));

    client.addModelFromS3(request, {}, (err, response) => {
      if (err) {
        console.error('[SeedGrpcService] AddModelFromS3 error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[SeedGrpcService] AddModelFromS3 success:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Remove a model by updating config without it
 */
export const removeModel = async (modelNameToRemove: string): Promise<any> => {
  console.log('[SeedGrpcService] Removing model:', modelNameToRemove);

  // Get current configs
  const currentConfigs = await getModelConfigs();

  // Filter out the model to remove
  const updatedConfigs = currentConfigs.configsList.filter(
    (config: any) => config.modelName !== modelNameToRemove
  );

  if (updatedConfigs.length === currentConfigs.configsList.length) {
    throw new Error(`Model '${modelNameToRemove}' not found`);
  }

  // Update with filtered list
  return updateModelConfigs(updatedConfigs);
};

/**
 * Get TaskType enum for use in UI
 */
export const TaskTypeEnum = {
  UNKNOWN: 0,
  SEMANTIC_SEGMENTATION: 1,
  IMAGE_REGENERATION: 2,
  OBJECT_DETECTION: 3,
  DAMAGE_CLASSIFICATION: 4,
};

/**
 * Convert TaskType from string name to numeric value
 */
const taskTypeToNumber = (taskType: any): number => {
  // If already a number, return it
  if (typeof taskType === 'number') {
    return taskType;
  }

  // If it's a string enum name, convert to number
  const stringToEnum: { [key: string]: number } = {
    'UNKNOWN': 0,
    'SEMANTIC_SEGMENTATION': 1,
    'IMAGE_REGENERATION': 2,
    'OBJECT_DETECTION': 3,
    'DAMAGE_CLASSIFICATION': 4,
  };

  return stringToEnum[taskType] !== undefined ? stringToEnum[taskType] : 0;
};

/**
 * Get TaskType display name
 */
export const getTaskTypeName = (taskType: number | string): string => {
  // Convert to number if it's a string
  const numericTaskType = taskTypeToNumber(taskType);

  const names: { [key: number]: string } = {
    0: 'Unknown',
    1: 'Semantic Segmentation',
    2: 'Image Regeneration',
    3: 'Object Detection',
    4: 'Damage Classification',
  };
  return names[numericTaskType] || 'Unknown';
};

export default {
  healthCheck,
  getModelConfigs,
  updateModelConfigs,
  addModelFromS3,
  removeModel,
  TaskTypeEnum,
  getTaskTypeName,
};
